<script setup lang="ts">
import config from "@/config";
import { useAppStore } from "@/store/app";
import type { GroupField } from "@/util/methods";
import { hasGroupAddress } from "@/util/sellProductAddress";
import {
  UIAlert,
  UIButton,
  UICollapse,
  UICollapseItem,
  UIDivider,
  UISelect,
  UISwitch,
  UITabPane,
  UITabs,
} from "@gohighlevel/ghl-ui";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { PaymentServices } from "@/service/PaymentServices";
import {
  type EntityProvidersApiResponse,
  resolveProvidersForChannel,
} from "@/util/resolveEntityProvidersForDisplay";
import { loadImages } from "@/util/paymentProviderImages";
import DonationSettings from "../Donation/DonationSettings.vue";
import Switch from "../Elements/Switch.vue";
import BumpSettings from "../Products/bump/bumpSettings.vue";
import ProductCardList from "../Products/ProductCardList.vue";
import ProductLayoutSettings from "../Products/ProductLayoutSettings.vue";
import ProductSettings from "../Products/ProductSettings.vue";
import CouponSettings from "./CouponSettings.vue";
import PaymentAddressSettings from "./PaymentAddressSettings.vue";
import PaymentFooter from "./PaymentFooter.vue";
import PaymentGeneralSettings from "./PaymentGeneralSettings.vue";
import ProductSelectionType from "./ProductSelectionType.vue";
import RequireCreditCardSetting from "./RequireCreditCardSetting.vue";

import paymentProvidersIcon from "@/assets/icons/payment-providers-icon.svg?url";

const { t } = useI18n();

const props = defineProps({
  payment: {
    type: Object,
    required: true,
  },
  selectedIndex: {
    type: Number,
    required: true,
  },
  selectedSlideIndex: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const store = useAppStore();
const paymentTypeOption = computed(() => [
  { label: t("common.payment.sellProducts"), value: "product" },
  { label: t("common.payment.collectPayment"), value: "donation" },
]);
const paymentTypeValue = computed({
  get: () => store.payment.paymentType || "donation",
  set: (type: string) => {
    store.payment.paymentType = type;
    store.anyUnsavedChanges = true;
  },
});

const currentProductSettings = ref<{ index: number | null; isBumpProduct: boolean }>({
  index: null,
  isBumpProduct: false,
});

const entityProvidersLoading = ref(false);
const failedProviderImages = ref<Set<string>>(new Set());

const entityProvidersData = ref<EntityProvidersApiResponse | null>(null);

const connectedProvidersList = computed(() => {
  const channel = store.surveyId ? "surveys" : "forms";
  return resolveProvidersForChannel(entityProvidersData.value, channel);
});

const addProduct = computed({
  get: () => store.showAddProductSettings,
  set: (value) => {
    store.showAddProductSettings = value;
  },
});

const providerImageSrc = (imageUrl: string) => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http")) return imageUrl;
  const base = config?.GHL_DOMAIN ? `https://${config.GHL_DOMAIN}` : "https://gohighlevel.com";
  return `${base}${imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`}`;
};

const getProviderImageSrc = (imageUrl: string) => {
  const loaded = loadImages(imageUrl);
  return loaded !== imageUrl ? loaded : providerImageSrc(imageUrl);
};

const markProviderImageFailed = (providerId: string) => {
  failedProviderImages.value = new Set([...failedProviderImages.value, providerId]);
};

/**
 * Fetches payment entity providers (forms or surveys) for the current location and live/test mode.
 */
const fetchEntityProviders = async () => {
  if (!store.locationId) return;
  entityProvidersLoading.value = true;
  failedProviderImages.value = new Set();
  try {
    const entityType = store.surveyId ? "surveys" : "forms";
    const { data } = await PaymentServices.getEntityProviders({
      locationId: store.locationId,
      liveMode: store.payment.liveModeOn ?? false,
      entityType,
    });
    entityProvidersData.value = data;
  } catch {
    entityProvidersData.value = null;
  } finally {
    entityProvidersLoading.value = false;
  }
};

onMounted(async () => {
  await fetchEntityProviders();
});

watch(
  () => store.locationId,
  (id) => {
    if (id) fetchEntityProviders();
  },
  { immediate: false }
);

watch(
  () => store.payment.liveModeOn,
  () => {
    fetchEntityProviders();
  }
);

const paymentField = computed(() => props.payment as GroupField);

const hasAddressField = computed(() =>
  hasGroupAddress(store.sellProductAddressSlides)
);

const billingAddressEnabled = computed(
  () => paymentField.value.billingAddressEnabled === true
);

const updatePaymentType = (type: string) => {
  if (type === paymentTypeValue.value) return;
  if (type !== "donation") {
    paymentTypeValue.value = type;
    return;
  }
  store.changeSellProductToDonation();
};

const updateBillingAddress = (enabled: boolean) => {
  if (enabled && !hasAddressField.value) return;
  store.setSellProductBillingAddress(
    props.selectedIndex,
    enabled,
    props.selectedSlideIndex
  );
};

const liveStatusUpdate = (isLiveOn: boolean) => {
  store.payment.liveModeOn = isLiveOn;
  store.anyUnsavedChanges = true;
};

const paymentProvidersUrl = () =>
  `https://${store.company?.domain || config.GHL_DOMAIN}/v2/location/${
    store.locationId
  }/payments/integrations/configure-providers`;

const openPaymentProvidersPage = () => {
  window.open(paymentProvidersUrl(), "_blank")?.focus();
};

const onConnectionClick = () => {
  openPaymentProvidersPage();
};

const onManageProvider = () => {
  openPaymentProvidersPage();
};
const onAddProduct = () => {
  addProduct.value = true;
};
const onBack = () => {
  addProduct.value = false;
  currentProductSettings.value.index = null;
  currentProductSettings.value.isBumpProduct = false;
};

const onProductSettings = (index: number, isBumpProduct: boolean) => {
  addProduct.value = true;
  currentProductSettings.value.index = index;
  currentProductSettings.value.isBumpProduct = isBumpProduct;
};

const handleShowInfoAlert = (value: boolean) => {
  store.payment.showInfoAboutPayment = value;
  store.anyUnsavedChanges = true;
};

const currentProduct = computed(() => {
  if (currentProductSettings.value.index === null) {
    return undefined;
  }
  return currentProductSettings.value.isBumpProduct
    ? store.bumProductList[currentProductSettings.value.index]
    : store.addedProductList[currentProductSettings.value.index];
});

</script>

<template>
  <h6 class="field-name-sidebar">
    {{ payment.label || payment.name }}
  </h6>
  <div
    v-if="!addProduct"
    id="payment-settings"
    class="payment-settings mb-4 flex flex-col items-start self-stretch"
  >
    <div class="flex flex-col items-start gap-3 self-stretch pt-2">
      <!-- Payment providers card (before Payment Type) -->
      <div
        class="payment-providers-card flex w-full flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4"
      >
        <div class="flex flex-col gap-2">
          <img
            :src="paymentProvidersIcon"
            class="h-8 w-8 shrink-0 self-start"
            alt=""
            aria-hidden="true"
          />
          <div class="flex flex-col gap-0.5">
            <span class="text-[14px] font-medium text-gray-900">
              {{ t("common.payment.paymentProviders") }}
            </span>
            <p class="text-sm text-gray-500">
              {{ t("common.payment.paymentProvidersDescription") }}
            </p>
          </div>
        </div>
        <div class="flex items-center justify-between self-stretch">
          <div class="flex items-center gap-2">
            <UISwitch :value="store.payment.liveModeOn" @update:value="liveStatusUpdate"></UISwitch>
            <span class="text-[14px] text-gray-900">{{ t("common.payment.liveModeOn") }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="!store.payment.liveModeOn"
              class="inline-flex items-center gap-1.5 rounded-full bg-[#FEF3F2] px-2.5 py-0.5 text-sm font-medium text-[#F04438]"
            >
              <span
                class="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F04438]"
                aria-hidden="true"
              />
              {{ t("common.payment.test") }}
            </span>
            <span
              v-else
              class="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF3] px-2.5 py-0.5 text-sm font-medium text-[#12B76A]"
            >
              <span
                class="h-1.5 w-1.5 shrink-0 rounded-full bg-[#12B76A]"
                aria-hidden="true"
              />
              {{ t("common.payment.live") }}
            </span>
          </div>
        </div>
        <div class="payment-connected-providers w-full overflow-hidden rounded-lg border border-gray-200">
          <UICollapse
            :accordion="true"
            :arrow-placement="'right'"
            :default-expanded-names="['connected-providers']"
            class="w-full self-stretch"
          >
            <UICollapseItem id="connected-providers" name="connected-providers">
              <template #header>
                <span class="normal-case text-sm font-medium text-gray-700">
                  {{ t("common.payment.connectedProviders") }}
                </span>
              </template>
              <div class="payment-connected-providers-list flex flex-col">
                <template v-if="entityProvidersLoading">
                  <span class="py-2 text-sm text-gray-500">{{ t("common.payment.loading") }}</span>
                </template>
                <template v-else-if="connectedProvidersList.length">
                  <div
                    v-for="(provider, index) in connectedProvidersList"
                    :key="`connected-provider-${provider.providerId}-${index}`"
                    class="payment-provider-row flex items-center justify-between gap-3"
                  >
                    <div class="flex min-w-0 flex-1 items-center gap-3">
                      <div class="flex h-6 w-8 shrink-0 items-center justify-center">
                        <img
                          v-if="provider.imageUrl && !failedProviderImages.has(provider.providerId)"
                          :src="getProviderImageSrc(provider.imageUrl)"
                          :alt="provider.title"
                          class="max-h-6 max-w-8 object-contain"
                          @error="markProviderImageFailed(provider.providerId)"
                        />
                      </div>
                      <span class="truncate text-sm font-medium text-gray-900">{{ provider.title }}</span>
                    </div>
                    <UIButton
                      :id="`payment-manage-provider-${provider.providerId}`"
                      type="text"
                      class="payment-manage-link shrink-0"
                      @click="onManageProvider"
                    >
                      {{ t("common.manage") }}
                    </UIButton>
                  </div>
                </template>
                <template v-else>
                  <span class="py-2 text-sm text-gray-500">
                    {{ t("common.payment.paymentProvidersDescription") }}
                  </span>
                </template>
              </div>
            </UICollapseItem>
          </UICollapse>
        </div>
        <UIButton
          id="payment-connect"
          type="primary"
          class="flex w-full items-center justify-center gap-3 self-stretch"
          @click="onConnectionClick"
        >
          <span class="mr-1 text-lg leading-none">+</span>
          {{ t("common.payment.connectProviders") }}
        </UIButton>
      </div>

      <div class="flex flex-col items-start justify-between gap-3 self-stretch">
        <div class="flex space-x-3">
          <span class="switch-handle mb-1 block text-sm font-medium text-gray-700">{{
            t("common.payment.paymentType")
          }}</span>
        </div>
        <UISelect
          id="paymentType"
          :value="paymentTypeValue"
          size="medium"
          :options="paymentTypeOption"
          @update:value="updatePaymentType"
        />
      </div>
      <div v-if="store.surveyId" class="flex flex-col gap-3">
        <UIAlert id="warning-message" type="default" :closable="false">
          <template #content>{{ t("common.paymentWillBeProcessedNextSlide") }}</template>
        </UIAlert>
        <Switch
          :label="t('common.showThisAlertToUsers')"
          :value="store.payment?.showInfoAboutPayment"
          @update:value="(value) => handleShowInfoAlert(value)"
        ></Switch>
      </div>

      <!-- General & Product tabs (same component as RightSidebar Styles/Themes/Options) -->
      <div
        v-if="paymentTypeValue === 'product'"
        id="payment-sidebar-tab"
        class="payment-sidebar-tab-container"
      >
        <UITabs justify-content="space-evenly" default-value="general">
          <UITabPane name="general" :tab="t('common.payment.generalTab')">
            <UICollapse
              :accordion="false"
              :arrow-placement="'left'"
              :default-expanded-names="['content', 'address', 'coupons', 'footer']"
              class="w-full self-stretch"
            >
              <UICollapseItem id="payment-product-general-content" name="content">
                <template #header>
                  <div>{{ t("common.content") }}</div>
                </template>
                <div class="flex flex-col gap-3 pt-2">
                  <PaymentGeneralSettings
                    :selected-index="props.selectedIndex"
                    :selected-slide-index="props.selectedSlideIndex"
                  />
                </div>
              </UICollapseItem>
              <UICollapseItem id="payment-product-general-address" name="address">
                <template #header>
                  <div class="flex items-center gap-1 normal-case">
                    <span>{{ t("common.address") }}</span>
                    <span
                      class="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-primary-700"
                    >
                      {{ t("common.new") }}
                    </span>
                  </div>
                </template>
                <PaymentAddressSettings
                  :has-address-field="hasAddressField"
                  :billing-enabled="billingAddressEnabled && hasAddressField"
                  @update:billing="updateBillingAddress"
                />
              </UICollapseItem>
              <UICollapseItem id="payment-product-general-coupons" name="coupons">
                <template #header>
                  <div>{{ t("common.payment.coupons.coupons") }}</div>
                </template>
                <div class="pt-2">
                  <CouponSettings />
                </div>
              </UICollapseItem>
              <UICollapseItem id="payment-product-general-footer" name="footer">
                <template #header>
                  <div>{{ t("common.payment.footerInfo") }}</div>
                </template>
                <div class="pt-2">
                  <PaymentFooter />
                </div>
              </UICollapseItem>
            </UICollapse>
          </UITabPane>
          <UITabPane name="product" :tab="t('common.payment.productTab')">
            <UICollapse
              :accordion="false"
              :arrow-placement="'left'"
              :default-expanded-names="['sellProducts', 'bumpProduct']"
              class="w-full self-stretch"
            >
              <UICollapseItem id="payment-product-sell-products" name="sellProducts">
                <template #header>
                  <div>{{ t("common.payment.sellProducts") }}</div>
                </template>
                <div class="payment-sell-products-settings flex flex-col gap-3 pt-2">
                  <UIButton
                    id="payment-add-product"
                    size="small"
                    type="tertiary"
                    class="flex items-center justify-center gap-2 self-stretch"
                    :disabled="store.addedProductList.length >= 20"
                    @click="onAddProduct"
                  >
                    {{ t("common.payment.product.addProduct") }}
                  </UIButton>
                  <UIAlert v-if="store.addedProductList.length >= 20" id="warning-message" type="warning">
                    <template #content>{{ t("common.payment.product.maxProductError") }}</template>
                  </UIAlert>
                  <ProductCardList
                    :products="store.payment.products"
                    @product:settings="onProductSettings"
                  />
                  <ProductLayoutSettings />
                  <ProductSelectionType />
                  <UIDivider />
                  <RequireCreditCardSetting
                    :selected-index="props.selectedIndex"
                    :selected-slide-index="props.selectedSlideIndex"
                  />
                </div>
              </UICollapseItem>
              <UICollapseItem id="payment-product-bump" name="bumpProduct">
                <template #header>
                  <div>{{ t("common.payment.bumProduct.bumpProduct") }}</div>
                </template>
                <div class="pt-2">
                  <BumpSettings />
                </div>
              </UICollapseItem>
            </UICollapse>
          </UITabPane>
        </UITabs>
      </div>

      <!-- Donation (Collect payment): General + Payment tabs -->
      <div
        v-if="paymentTypeValue === 'donation'"
        id="payment-sidebar-tab-donation"
        class="payment-sidebar-tab-container"
      >
        <UITabs justify-content="space-evenly" default-value="general">
          <UITabPane name="general" :tab="t('common.payment.generalTab')">
            <UICollapse
              :accordion="false"
              :arrow-placement="'left'"
              :default-expanded-names="['content', 'coupons', 'footer']"
              class="w-full self-stretch"
            >
              <UICollapseItem id="payment-donation-general-content" name="content">
                <template #header>
                  <div>{{ t("common.content") }}</div>
                </template>
                <div class="flex flex-col gap-3 pt-2">
                  <PaymentGeneralSettings
                    :selected-index="props.selectedIndex"
                    :selected-slide-index="props.selectedSlideIndex"
                  />
                </div>
              </UICollapseItem>
              <UICollapseItem id="payment-donation-general-coupons" name="coupons">
                <template #header>
                  <div>{{ t("common.payment.coupons.coupons") }}</div>
                </template>
                <div class="pt-2">
                  <CouponSettings />
                </div>
              </UICollapseItem>
              <UICollapseItem id="payment-donation-general-footer" name="footer">
                <template #header>
                  <div>{{ t("common.payment.footerInfo") }}</div>
                </template>
                <div class="pt-2">
                  <PaymentFooter />
                </div>
              </UICollapseItem>
            </UICollapse>
          </UITabPane>
          <UITabPane name="payment" :tab="t('common.payment.payments')">
            <DonationSettings />
          </UITabPane>
        </UITabs>
      </div>
    </div>
  </div>
  <ProductSettings
    v-if="addProduct"
    class="product-sidebar"
    :current-product="currentProduct"
    @on-back="onBack"
  ></ProductSettings>
</template>

<style>
.sub-header {
  border-bottom: 1px solid #d0d5dd;
  margin: 0 -16px 17px -16px;
}
.select-suffix {
  border: 0px solid transparent;
  outline: none;
  border-radius: 0;
}

.center-btn {
  margin: 0 auto;
}
.product-sidebar {
  position: fixed;
  right: 0;
  top: 98px;
  width: 100%;
  max-width: 360px;
  background-color: #ffffff;
  border: 1px solid #dee2e6 !important;
  overflow-y: scroll;
  height: 95vh;
  padding: 15px;
  padding-bottom: 60px;
}

.sub-settings-header {
  border-bottom: 1px solid #d0d5dd;
  display: flex;
  padding: 12px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  margin: 0 -15px;
}

/* Same as RightSidebar.vue #sidebar-tab so UITabs matches Styles/Themes/Options */
.payment-sidebar-tab-container {
  font-size: 14px;
  width: 100%;
}
#payment-sidebar-tab .n-tabs-nav,
#payment-sidebar-tab-donation .n-tabs-nav {
  width: 100%;
  z-index: 1;
  background: #ffffff;
  left: 0;
}
/* Equal-width block-style tabs (General and Product each take 50%) */
#payment-sidebar-tab .n-tabs-wrapper,
#payment-sidebar-tab-donation .n-tabs-wrapper {
  display: flex;
  width: 100%;
}
#payment-sidebar-tab .n-tabs-tab-wrapper,
#payment-sidebar-tab-donation .n-tabs-tab-wrapper {
  flex: 1;
  min-width: 0;
}
#payment-sidebar-tab .n-tabs-tab,
#payment-sidebar-tab-donation .n-tabs-tab {
  width: 100%;
  display: flex;
  justify-content: center;
}
/* Ensure General/Product tab bar is visible when rendered inside FormCustomSidebar/SurveyCustomSidebar */
#payment-sidebar-tab .n-tabs-nav-scroll-wrapper,
#payment-sidebar-tab-donation .n-tabs-nav-scroll-wrapper {
  display: block !important;
}

/* Override naive-ui `.n-collapse .n-collapse-item .n-collapse-item { margin-left: 32px }`
   when PaymentSettings collapses render inside the field sidebar UICollapseItem */
#payment-settings .n-collapse > .n-collapse-item {
  margin-left: 0 !important;
}

/* naive-ui gives horizontal dividers 24px vertical margins. Inside this
   `gap-3` column that reads as a section break, so collapse it and let the
   flex gap own the spacing. UIDivider sets `inheritAttrs: false`, so the
   override has to target `.n-divider` from the parent rather than a class
   passed to the component. */
.payment-sell-products-settings > .n-divider {
  margin-top: 0;
  margin-bottom: 0;
}

.payment-providers-card .payment-connected-providers .n-collapse-item {
  margin-left: 0 !important;
  margin-top: 0 !important;
}

.payment-providers-card .payment-connected-providers .n-collapse-item__header {
  display: flex;
  align-items: center;
  padding: 12px 16px !important;
  border: none;
  background: transparent;
}

.payment-providers-card .payment-connected-providers .n-collapse-item__header-main {
  flex: 1;
  padding: 0;
  margin: 0;
  text-transform: none;
}

.payment-providers-card .payment-connected-providers .n-collapse-item-arrow {
  margin-left: 8px;
}

.payment-providers-card .payment-connected-providers .n-collapse-item__content-wrapper,
.payment-providers-card .payment-connected-providers .n-collapse-item__content-inner {
  padding: 0 !important;
}

.payment-providers-card .payment-connected-providers .n-collapse-item:not(:first-child) {
  border-top: none;
}

.payment-providers-card .payment-connected-providers-list {
  padding: 0 16px 12px;
}

.payment-providers-card .payment-provider-row {
  min-height: 40px;
  padding: 8px 0;
}

.payment-providers-card .payment-provider-row:not(:last-child) {
  border-bottom: 1px solid #f2f4f7;
}

.payment-providers-card .payment-manage-link {
  padding: 0 !important;
  min-height: auto !important;
  height: auto !important;
  color: #155eef !important;
  font-size: 14px !important;
  font-weight: 500 !important;
}

.payment-providers-card .payment-manage-link:hover {
  text-decoration: underline;
}
</style>
